import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
  BookOpen, 
  Lightbulb, 
  Target,
  User,
  DollarSign,
  Clock,
  CheckCircle2,
  TrendingUp,
  Zap,
  Users,
  Award
} from "lucide-react";

interface MetodologiaInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MetodologiaInfoModal({ open, onOpenChange }: MetodologiaInfoModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-primary" />
            Metodologias SPIN Selling e BANT
          </DialogTitle>
          <DialogDescription>
            Entenda as metodologias que fundamentam nosso processo de vendas
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="h-[calc(90vh-120px)] pr-4">
          <div className="space-y-6">
            {/* SPIN Selling */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl flex items-center gap-2 mb-2">
                      <Target className="h-5 w-5 text-blue-600" />
                      SPIN Selling
                    </CardTitle>
                    <CardDescription className="text-base">
                      Metodologia criada por <strong>Neil Rackham</strong> na década de 1980
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="text-sm">
                    <Award className="h-3 w-3 mr-1" />
                    Criada por Neil Rackham
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-blue-600">
                    <Lightbulb className="h-4 w-4" />
                    O que é SPIN Selling?
                  </h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    SPIN Selling é uma metodologia de vendas consultivas baseada em <strong>4 tipos de perguntas estratégicas</strong> que 
                    guiam o cliente através de um processo de descoberta e tomada de decisão. Foi desenvolvida após pesquisas com mais de 
                    35.000 chamadas de vendas em diferentes indústrias.
                  </p>
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2 text-blue-600">
                    <CheckCircle2 className="h-4 w-4" />
                    Os 4 Tipos de Perguntas SPIN
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="bg-white">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">S</span>
                          Situação (Situation)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        <p className="mb-2">Compreender o contexto atual do cliente:</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Consumo energético atual</li>
                          <li>Custos com energia</li>
                          <li>Infraestrutura existente</li>
                          <li>Histórico de contas</li>
                        </ul>
                        <p className="mt-2 text-xs font-medium">Objetivo: Estabelecer baseline para análise</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-white">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">P</span>
                          Problema (Problem)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        <p className="mb-2">Identificar dificuldades e insatisfações:</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Aumentos constantes na conta</li>
                          <li>Impacto no orçamento</li>
                          <li>Preocupações com sustentabilidade</li>
                          <li>Dependência da concessionária</li>
                        </ul>
                        <p className="mt-2 text-xs font-medium">Objetivo: Revelar necessidades implícitas</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-white">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">I</span>
                          Implicação (Implication)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        <p className="mb-2">Explorar consequências dos problemas:</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Impacto financeiro futuro</li>
                          <li>Riscos operacionais</li>
                          <li>Custo de não agir</li>
                          <li>Perda de competitividade</li>
                        </ul>
                        <p className="mt-2 text-xs font-medium">Objetivo: Aumentar urgência de resolver</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-white">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">N</span>
                          Necessidade de Solução (Need-Payoff)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        <p className="mb-2">Demonstrar valor da solução:</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Economia financeira</li>
                          <li>Previsibilidade de custos</li>
                          <li>Sustentabilidade</li>
                          <li>Valorização do imóvel</li>
                        </ul>
                        <p className="mt-2 text-xs font-medium">Objetivo: Criar desejo pela solução</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-green-600">
                    <Zap className="h-4 w-4" />
                    Por que SPIN funciona para Energia Solar?
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-start gap-2 p-3 bg-white rounded-lg">
                      <TrendingUp className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                      <div className="text-sm">
                        <strong>Vendas Complexas:</strong> Ciclo longo (3-6 meses) requer abordagem consultiva
                      </div>
                    </div>
                    <div className="flex items-start gap-2 p-3 bg-white rounded-lg">
                      <DollarSign className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                      <div className="text-sm">
                        <strong>Alto Investimento:</strong> Requer justificativa clara de valor e ROI
                      </div>
                    </div>
                    <div className="flex items-start gap-2 p-3 bg-white rounded-lg">
                      <BookOpen className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                      <div className="text-sm">
                        <strong>Educação do Cliente:</strong> Cliente precisa entender tecnologia e benefícios
                      </div>
                    </div>
                    <div className="flex items-start gap-2 p-3 bg-white rounded-lg">
                      <Users className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                      <div className="text-sm">
                        <strong>Múltiplos Stakeholders:</strong> Vários decisores precisam ser convencidos
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* BANT */}
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl flex items-center gap-2 mb-2">
                      <Target className="h-5 w-5 text-green-600" />
                      Framework BANT
                    </CardTitle>
                    <CardDescription className="text-base">
                      Metodologia de qualificação de leads amplamente utilizada em vendas B2B
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="text-sm">
                    <Award className="h-3 w-3 mr-1" />
                    Framework Consagrado
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-green-600">
                    <Lightbulb className="h-4 w-4" />
                    O que é BANT?
                  </h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    BANT é um framework de qualificação de leads criado pela <strong>IBM</strong> na década de 1950 e 
                    amplamente utilizado em vendas B2B. A sigla representa os 4 critérios essenciais para determinar se 
                    um lead está qualificado para avançar no processo de vendas.
                  </p>
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    Os 4 Critérios BANT
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="bg-white">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <DollarSign className="h-5 w-5 text-blue-600" />
                          B - Budget (Orçamento)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        <p className="mb-2">O lead tem orçamento disponível?</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Já pesquisou sobre valores?</li>
                          <li>Possui orçamento aprovado?</li>
                          <li>Precisará de financiamento?</li>
                          <li>Qual faixa de investimento está considerando?</li>
                        </ul>
                        <p className="mt-2 text-xs font-medium text-blue-600">Sem orçamento, não há venda possível</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-white">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <User className="h-5 w-5 text-purple-600" />
                          A - Authority (Autoridade)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        <p className="mb-2">É o tomador de decisão?</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>É responsável por tomar a decisão?</li>
                          <li>Quem mais participa da decisão?</li>
                          <li>Qual o processo de aprovação?</li>
                          <li>Tem acesso ao decisor final?</li>
                        </ul>
                        <p className="mt-2 text-xs font-medium text-purple-600">Decisão sem autoridade não fecha</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-white">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Target className="h-5 w-5 text-orange-600" />
                          N - Need (Necessidade)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        <p className="mb-2">Existe necessidade real?</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Por que está interessado em energia solar?</li>
                          <li>Qual o principal problema com a conta?</li>
                          <li>Qual a dor principal que quer resolver?</li>
                          <li>Existe urgência ou é apenas curiosidade?</li>
                        </ul>
                        <p className="mt-2 text-xs font-medium text-orange-600">Sem necessidade real, não há motivação</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-white">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Clock className="h-5 w-5 text-red-600" />
                          T - Timeline (Prazo)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        <p className="mb-2">Qual o prazo para decisão?</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Qual o prazo para tomar a decisão?</li>
                          <li>Existe alguma urgência?</li>
                          <li>Já possui outras propostas?</li>
                          <li>Quando pretende instalar?</li>
                        </ul>
                        <p className="mt-2 text-xs font-medium text-red-600">Sem prazo, não há urgência</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-green-600">
                    <Zap className="h-4 w-4" />
                    Por que BANT funciona para Energia Solar?
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-start gap-2 p-3 bg-white rounded-lg">
                      <DollarSign className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                      <div className="text-sm">
                        <strong>Alto Investimento:</strong> Requer confirmação de orçamento antes de prosseguir
                      </div>
                    </div>
                    <div className="flex items-start gap-2 p-3 bg-white rounded-lg">
                      <Users className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                      <div className="text-sm">
                        <strong>Decisão Familiar/Empresarial:</strong> Múltiplos decisores envolvidos
                      </div>
                    </div>
                    <div className="flex items-start gap-2 p-3 bg-white rounded-lg">
                      <Target className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                      <div className="text-sm">
                        <strong>Qualificação Clara:</strong> Separa leads quentes de frios rapidamente
                      </div>
                    </div>
                    <div className="flex items-start gap-2 p-3 bg-white rounded-lg">
                      <Clock className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                      <div className="text-sm">
                        <strong>Priorização de Esforços:</strong> Foca em leads com maior potencial de fechamento
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-blue-600">
                    <Lightbulb className="h-4 w-4" />
                    Como usamos BANT + SPIN juntos?
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Utilizamos <strong>BANT</strong> na fase de qualificação inicial para identificar leads com potencial real. 
                    Em seguida, aplicamos <strong>SPIN Selling</strong> durante todo o processo de vendas para descobrir necessidades, 
                    criar urgência e demonstrar valor. Esta combinação garante que focamos nossos esforços em leads qualificados 
                    enquanto aplicamos uma abordagem consultiva que maximiza conversões.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}















